import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Home, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InterestCard = ({ type, icon: Icon, title, body, delay }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/interest`, {
        list_type: type,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        message: form.message || null,
      });
      toast.success(`You're on the ${type} interest list. We'll reach out with next steps.`);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Reveal delay={delay} className="h-full">
      <div
        className="h-full bg-card border border-foreground/10 rounded-3xl p-8 lg:p-10 flex flex-col transition-[border-color,transform] duration-500 hover:border-primary/30 hover:-translate-y-1"
        data-testid={`interest-card-${type}`}
      >
        <div className="flex items-center gap-4 mb-5">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/10 text-primary">
            <Icon size={20} />
          </span>
          <h3 className="font-serif font-light text-2xl lg:text-3xl leading-tight">{title}</h3>
        </div>
        <p className="text-sm lg:text-base leading-relaxed text-muted-foreground mb-8">{body}</p>

        <form onSubmit={submit} className="space-y-5 mt-auto" data-testid={`interest-form-${type}`}>
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              type="text"
              placeholder="Full name"
              value={form.name}
              onChange={set("name")}
              data-testid={`interest-name-input-${type}`}
              className="field-input"
            />
            <input
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={set("email")}
              data-testid={`interest-email-input-${type}`}
              className="field-input"
            />
          </div>
          <input
            type="tel"
            placeholder="Phone (optional)"
            value={form.phone}
            onChange={set("phone")}
            data-testid={`interest-phone-input-${type}`}
            className="field-input"
          />
          <textarea
            rows={2}
            placeholder="Anything we should know? (optional)"
            value={form.message}
            onChange={set("message")}
            data-testid={`interest-message-input-${type}`}
            className="field-input resize-none"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid={`interest-submit-btn-${type}`}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm font-medium text-primary-foreground transition-[background-color,transform] duration-300 hover:bg-lavender-deep hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Joining…" : "Join this list"}
          </button>
        </form>
      </div>
    </Reveal>
  );
};

const InterestList = () => (
  <section id="interest" data-testid="interest-section" className="py-28 lg:py-40">
    <div className="max-w-[90rem] mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-16 lg:mb-24">
        <Reveal>
          <p className="overline-label mb-6" data-testid="interest-overline">
            Adam's Way Interest Lists
          </p>
          <h2
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
            data-testid="interest-heading"
          >
            Picture yourself{" "}
            <em className="text-primary font-normal">here.</em>
          </h2>
          <p className="mt-6 text-base lg:text-lg leading-relaxed text-muted-foreground">
            Adam's Way will welcome residents with and without disabilities,
            some renting, some owning. Join the list that fits you and be the
            first to hear about availability, eligibility, and move-in timelines.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-12 gap-8 lg:gap-12">
        <div className="col-span-12 lg:col-span-6">
          <InterestCard
            type="rental"
            icon={KeyRound}
            title="Rental Opportunities"
            body="Interested in renting a home at Adam's Way? Join this list to learn about rental availability, supports, and the application process as homes near completion."
            delay={0.05}
          />
        </div>
        <div className="col-span-12 lg:col-span-6">
          <InterestCard
            type="purchase"
            icon={Home}
            title="Purchase Opportunities"
            body="Interested in owning a home in this one-of-a-kind inclusive community? Join this list for details on purchase opportunities, pricing, and timelines."
            delay={0.15}
          />
        </div>
      </div>
    </div>
  </section>
);

export default InterestList;
